import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BackButton, HomeButton } from '../components/NavButtons';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { format } from 'date-fns';
import { getCurrencySymbol, getSymbol } from '../utils/currency';
import {
  CalendarBlank, Clock, MapPin, QrCode, X, Check, Warning,
  House, MagnifyingGlass, List, User, ChatCircle, Star, Heart,
  ShareNetwork, Copy, CreditCard, Prohibit, ArrowRight, CaretRight,
  Wallet, ChartLineUp, Megaphone
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import AddToCalendar from '../components/AddToCalendar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  pending_payment: { label: 'Pending Payment', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  pending_approval: { label: 'Awaiting Approval', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  active: { label: 'Active', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-500' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
};

const MyBookings = () => {
  const navigate = useNavigate();
  const { user, isSeller } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewedBookings, setReviewedBookings] = useState(new Set());
  const [qrData, setQrData] = useState(null);
  const [stats, setStats] = useState({ upcoming: 0, active: 0, completed: 0, total_spent: 0 });

  useEffect(() => {
    if (user) fetchBookings();
  }, [user]);

  useEffect(() => {
    if (selectedBooking?.status === 'confirmed' && selectedBooking.check_in_code) {
      setQrData(null);
      axios.get(`${API_URL}/api/bookings/${selectedBooking.booking_id}/qr-data`)
        .then(res => setQrData(res.data.qr_base64))
        .catch(err => {
          console.warn('QR code fetch failed for booking', selectedBooking.booking_id, err?.response?.status, err?.message);
        });
    } else {
      setQrData(null);
    }
  }, [selectedBooking]);

  const fetchBookings = async () => {
    try {
      const [bookingsRes, reviewsRes] = await Promise.all([
        axios.get(`${API_URL}/api/bookings`),
        axios.get(`${API_URL}/api/reviews/my/all`),
      ]);
      const allBookings = bookingsRes.data.bookings || [];
      setBookings(allBookings);
      const reviewedIds = new Set((reviewsRes.data.reviews || []).map(r => r.booking_id));
      setReviewedBookings(reviewedIds);

      // Compute stats
      const now = new Date();
      const upcomingCount = allBookings.filter(b =>
        ['pending', 'confirmed', 'pending_payment', 'pending_approval'].includes(b.status) && new Date(b.start_date) >= now
      ).length;
      const activeCount = allBookings.filter(b =>
        b.status === 'active' || (b.status === 'confirmed' && new Date(b.start_date) <= now && new Date(b.end_date) >= now)
      ).length;
      const completedCount = allBookings.filter(b => b.status === 'completed').length;
      const totalSpent = allBookings.filter(b => ['confirmed', 'completed', 'active'].includes(b.status))
        .reduce((sum, b) => sum + (b.total_price || 0), 0);
      setStats({ upcoming: upcomingCount, active: activeCount, completed: completedCount, total_spent: totalSpent });
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    try {
      await axios.post(`${API_URL}/api/bookings/${bookingId}/cancel`, {});
      setBookings(prev => prev.map(b => b.booking_id === bookingId ? { ...b, status: 'cancelled' } : b));
      setSelectedBooking(null);
      toast.success('Booking cancelled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const shareBooking = async (booking) => {
    const text = `Booking at ${booking.listing_title}\nDate: ${format(new Date(booking.start_date), 'MMM d, yyyy')}\nTime: ${booking.start_time} - ${booking.end_time}\nStatus: ${STATUS_MAP[booking.status]?.label || booking.status}\nBooking ID: ${booking.booking_id}`;
    const url = `${window.location.origin}/listing/${booking.listing_id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `Booking - ${booking.listing_title}`, text, url });
      } catch (e) { if (e?.name !== 'AbortError') console.error('Share failed:', e?.message); }
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success('Booking details copied to clipboard!');
    }
  };

  const filterBookings = (tab) => {
    const now = new Date();
    switch (tab) {
      case 'upcoming':
        return bookings.filter(b =>
          ['pending', 'confirmed', 'pending_payment', 'pending_approval'].includes(b.status) &&
          new Date(b.start_date) >= new Date(now.toISOString().split('T')[0])
        );
      case 'active':
        return bookings.filter(b =>
          b.status === 'active' ||
          (b.status === 'confirmed' && new Date(b.start_date) <= now && new Date(b.end_date) >= now)
        );
      case 'completed':
        return bookings.filter(b => b.status === 'completed');
      case 'cancelled':
        return bookings.filter(b => b.status === 'cancelled');
      default:
        return bookings;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700 mb-4">Please sign in to view your bookings</h2>
          <Button onClick={() => navigate('/buyer/login')} className="bg-blue-500 hover:bg-blue-600 rounded-full">Sign In</Button>
        </div>
      </div>
    );
  }

  const TABS = [
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'active', label: 'Active' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BackButton />
            <HomeButton />
            <h1 className="text-lg font-bold text-slate-900">My Bookings</h1>
          </div>
          <div className="flex items-center gap-2">
            {isSeller && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/seller')}
                className="text-emerald-600 hover:text-emerald-700 text-xs" data-testid="switch-seller-btn">
                Seller Mode <ArrowRight size={14} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-4 space-y-5">
        {/* Quick Stats Dashboard */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="buyer-stats">
          <div className="bg-white rounded-xl p-3.5 border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-slate-500 uppercase">Upcoming</span>
              <CalendarBlank size={16} className="text-blue-400" />
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="stat-upcoming">{stats.upcoming}</p>
          </div>
          <div className="bg-white rounded-xl p-3.5 border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-slate-500 uppercase">Active</span>
              <Clock size={16} className="text-emerald-400" />
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="stat-active">{stats.active}</p>
          </div>
          <div className="bg-white rounded-xl p-3.5 border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-slate-500 uppercase">Completed</span>
              <Check size={16} className="text-slate-400" weight="bold" />
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="stat-completed">{stats.completed}</p>
          </div>
          <div className="bg-white rounded-xl p-3.5 border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-slate-500 uppercase">Total Spent</span>
              <Wallet size={16} className="text-amber-400" />
            </div>
            <p className="text-xl font-bold text-slate-900" data-testid="stat-spent">
              {getSymbol(user?.country)}{stats.total_spent.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Quick Links */}
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="quick-links">
          <button onClick={() => navigate('/buyer')} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600 whitespace-nowrap" data-testid="link-explore">
            <MagnifyingGlass size={14} /> Explore Spaces
          </button>
          <button onClick={() => navigate('/wishlist')} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:border-red-300 hover:text-red-600 whitespace-nowrap" data-testid="link-wishlist">
            <Heart size={14} /> Saved Spaces
          </button>
          <button onClick={() => navigate('/messages')} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:border-purple-300 hover:text-purple-600 whitespace-nowrap" data-testid="link-messages">
            <ChatCircle size={14} /> Messages
          </button>
          <button onClick={() => navigate('/profile')} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-600 hover:border-slate-400 whitespace-nowrap" data-testid="link-profile">
            <User size={14} /> Profile
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-200" data-testid="booking-tabs">
          {TABS.map(tab => {
            const count = filterBookings(tab.key).length;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === tab.key
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`tab-${tab.key}`}>
                {tab.label} {count > 0 && <span className="ml-1 opacity-80">({count})</span>}
              </button>
            );
          })}
        </div>

        {/* Booking List */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={`booking-skeleton-${i}`} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="flex gap-3"><div className="w-20 h-20 bg-slate-200 rounded-lg" /><div className="flex-1 space-y-2"><div className="h-4 bg-slate-200 rounded w-3/4" /><div className="h-3 bg-slate-200 rounded w-1/2" /></div></div>
              </div>
            ))}
          </div>
        ) : filterBookings(activeTab).length > 0 ? (
          <div className="space-y-3">
            {filterBookings(activeTab).map(booking => (
              <BookingCard
                key={booking.booking_id}
                booking={booking}
                onClick={() => setSelectedBooking(booking)}
                onCancel={() => cancelBooking(booking.booking_id)}
                onShare={() => shareBooking(booking)}
                isReviewed={reviewedBookings.has(booking.booking_id)}
                onReview={() => navigate(`/bookings/${booking.booking_id}/review`)}
                onPay={() => navigate(`/listing/${booking.listing_id}`)}
                cs={getSymbol(booking.currency || booking.country)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-100">
            <CalendarBlank size={40} className="text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-slate-700 mb-1">No {activeTab} bookings</h3>
            <p className="text-sm text-slate-400 mb-4">
              {activeTab === 'upcoming' ? 'Start exploring and book your first space!' : `You don't have any ${activeTab} bookings`}
            </p>
            {activeTab === 'upcoming' && (
              <Button onClick={() => navigate('/buyer')} className="bg-blue-500 hover:bg-blue-600 rounded-full text-sm" data-testid="explore-btn">
                <MagnifyingGlass size={16} className="mr-1.5" /> Explore Spaces
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setSelectedBooking(null)}>
          <div className="bg-white w-full sm:w-[500px] sm:rounded-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-bold text-slate-900">Booking Details</h3>
              <button onClick={() => setSelectedBooking(null)} className="p-2 hover:bg-slate-100 rounded-full" data-testid="close-modal-btn">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Status + Share */}
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STATUS_MAP[selectedBooking.status]?.color || 'bg-slate-100 text-slate-700'}`}>
                  {STATUS_MAP[selectedBooking.status]?.label || selectedBooking.status}
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={() => shareBooking(selectedBooking)}
                    className="p-2 hover:bg-blue-50 rounded-lg text-blue-500" title="Share" data-testid="share-booking-btn">
                    <ShareNetwork size={18} weight="bold" />
                  </button>
                  <span className="text-xs text-slate-400">ID: {selectedBooking.booking_id.slice(0, 12)}</span>
                </div>
              </div>

              {/* Space Info */}
              <div className="flex gap-3 cursor-pointer" onClick={() => { setSelectedBooking(null); navigate(`/listing/${selectedBooking.listing_id}`); }}>
                {selectedBooking.listing_image ? (
                  <img src={selectedBooking.listing_image} alt={selectedBooking.listing_title} className="w-20 h-20 rounded-xl object-cover" />
                ) : (
                  <div className="w-20 h-20 bg-slate-100 rounded-xl flex items-center justify-center">
                    <House size={32} className="text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900">{selectedBooking.listing_title}</h4>
                  <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                    <MapPin size={14} /> {selectedBooking.listing_address}
                  </p>
                  <p className="text-xs text-blue-500 mt-1">View listing <ArrowRight size={12} className="inline" /></p>
                </div>
              </div>

              {/* Date & Time */}
              <div className="bg-slate-50 rounded-xl p-3.5 space-y-2.5">
                <div className="flex items-center gap-3 text-sm">
                  <CalendarBlank size={18} className="text-blue-500" />
                  <span className="text-slate-700">{format(new Date(selectedBooking.start_date), 'EEEE, MMMM d, yyyy')}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock size={18} className="text-blue-500" />
                  <span className="text-slate-700">{selectedBooking.start_time} - {selectedBooking.end_time}</span>
                </div>
                {selectedBooking.guests > 1 && (
                  <div className="flex items-center gap-3 text-sm">
                    <User size={18} className="text-blue-500" />
                    <span className="text-slate-700">{selectedBooking.guests} guests</span>
                  </div>
                )}
              </div>

              {/* QR Check-in */}
              {selectedBooking.status === 'confirmed' && selectedBooking.check_in_code && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center" data-testid="qr-checkin-section">
                  <p className="text-sm text-blue-600 mb-2 font-medium">QR Check-in</p>
                  {qrData ? (
                    <img src={qrData} alt="QR Check-in" className="w-36 h-36 mx-auto mb-2" data-testid="qr-code-image" />
                  ) : (
                    <div className="w-36 h-36 mx-auto mb-2 bg-white rounded-xl flex items-center justify-center">
                      <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mb-1">Show this QR to the seller at check-in</p>
                  <p className="text-lg font-bold text-blue-700 tracking-widest">{selectedBooking.check_in_code}</p>
                  {selectedBooking.checked_in && (
                    <div className="mt-2 px-3 py-1.5 bg-emerald-100 rounded-lg inline-flex items-center gap-1.5">
                      <Check size={14} className="text-emerald-600" weight="bold" />
                      <span className="text-emerald-700 text-xs font-semibold">Checked In</span>
                    </div>
                  )}
                </div>
              )}

              {/* Add to Calendar (for confirmed or upcoming bookings) */}
              {['confirmed', 'pending_approval', 'pending_payment'].includes(selectedBooking.status) && (
                <div className="border border-slate-200 rounded-xl p-3" data-testid="mybookings-add-to-calendar">
                  <AddToCalendar
                    icsPath={`/api/bookings/${selectedBooking.booking_id}/calendar.ics`}
                    title={`Booking: ${selectedBooking.listing_title || 'Space'}`}
                    location={selectedBooking.listing_address || ''}
                    description={`Booking ID: ${selectedBooking.booking_id}\nCheck-in code: ${selectedBooking.check_in_code || ''}`}
                    startISO={`${selectedBooking.start_date}T${selectedBooking.start_time || '00:00'}:00`}
                    endISO={`${selectedBooking.end_date || selectedBooking.start_date}T${selectedBooking.end_time || selectedBooking.start_time || '00:00'}:00`}
                    reminderText="Reminder will fire 1 hour before your booking."
                  />
                </div>
              )}

              {/* Payment notice for pending_payment */}
              {selectedBooking.status === 'pending_payment' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3" data-testid="payment-notice">
                  <CreditCard size={24} className="text-amber-600 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Payment Required</p>
                    <p className="text-xs text-amber-600">Complete payment to confirm your booking</p>
                  </div>
                </div>
              )}

              {/* Price */}
              <div className="flex items-center justify-between py-3 border-t border-slate-100">
                <span className="text-slate-600 text-sm">Total</span>
                <span className="text-xl font-bold text-slate-900">
                  {getSymbol(selectedBooking.currency || selectedBooking.country)}{selectedBooking.total_price?.toLocaleString()}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                {['pending', 'confirmed', 'pending_payment', 'pending_approval'].includes(selectedBooking.status) && (
                  <Button
                    variant="outline"
                    onClick={() => cancelBooking(selectedBooking.booking_id)}
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-xl h-10"
                    data-testid="cancel-booking-btn"
                  >
                    <Prohibit size={16} className="mr-1.5" /> Cancel
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => shareBooking(selectedBooking)}
                  className="border-blue-200 text-blue-600 hover:bg-blue-50 rounded-xl h-10 px-4"
                  data-testid="share-booking-modal-btn"
                >
                  <ShareNetwork size={16} className="mr-1.5" /> Share
                </Button>
                {selectedBooking.status === 'completed' && !reviewedBookings.has(selectedBooking.booking_id) && (
                  <Button
                    onClick={() => { setSelectedBooking(null); navigate(`/bookings/${selectedBooking.booking_id}/review`); }}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 rounded-xl h-10"
                    data-testid="modal-write-review-btn"
                  >
                    <Star size={16} className="mr-1.5" weight="fill" /> Review
                  </Button>
                )}
                {selectedBooking.status === 'completed' && reviewedBookings.has(selectedBooking.booking_id) && (
                  <Button
                    variant="outline"
                    onClick={() => { setSelectedBooking(null); navigate(`/bookings/${selectedBooking.booking_id}/review`); }}
                    className="border-emerald-200 text-emerald-700 rounded-xl h-10 px-4"
                    data-testid="modal-view-review-btn"
                  >
                    <Check size={16} className="mr-1.5" weight="bold" /> Reviewed
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40">
        <div className="max-w-5xl mx-auto flex items-center justify-around h-14">
          <Link to="/buyer" className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-blue-600" data-testid="nav-home">
            <House size={22} />
            <span className="text-[10px] font-medium">Home</span>
          </Link>
          <Link to="/search" className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-blue-600" data-testid="nav-search">
            <MagnifyingGlass size={22} />
            <span className="text-[10px] font-medium">Search</span>
          </Link>
          <Link to="/bookings" className="flex flex-col items-center gap-0.5 text-blue-600" data-testid="nav-bookings">
            <List size={22} weight="fill" />
            <span className="text-[10px] font-semibold">Bookings</span>
          </Link>
          <Link to="/messages" className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-blue-600" data-testid="nav-messages">
            <ChatCircle size={22} />
            <span className="text-[10px] font-medium">Messages</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-blue-600" data-testid="nav-profile">
            <User size={22} />
            <span className="text-[10px] font-medium">Profile</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

/* ==============================
 * BOOKING CARD COMPONENT
 * ============================== */
const BookingCard = ({ booking, onClick, onCancel, onShare, isReviewed, onReview, onPay, cs }) => {
  const statusInfo = STATUS_MAP[booking.status] || { label: booking.status, color: 'bg-slate-100 text-slate-700' };
  const canCancel = ['pending', 'confirmed', 'pending_payment', 'pending_approval'].includes(booking.status);

  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`booking-card-${booking.booking_id}`}>
      <div className="p-3.5 cursor-pointer" onClick={onClick}>
        <div className="flex gap-3">
          {booking.listing_image ? (
            <img src={booking.listing_image} alt={booking.listing_title} className="w-20 h-20 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
              <House size={28} className="text-slate-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-slate-900 text-sm truncate">{booking.listing_title}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
              <CalendarBlank size={12} />
              {format(new Date(booking.start_date), 'MMM d, yyyy')}
              <span className="text-slate-300 mx-0.5">|</span>
              <Clock size={12} />
              {booking.start_time} - {booking.end_time}
            </p>
            <p className="text-sm font-bold text-slate-900 mt-1.5">{cs}{booking.total_price?.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Card Actions */}
      <div className="px-3.5 pb-3 flex items-center gap-2 border-t border-slate-50 pt-2">
        {canCancel && (
          <button onClick={(e) => { e.stopPropagation(); onCancel(); }}
            className="px-3 py-1.5 rounded-lg border border-red-200 text-red-500 text-[11px] font-semibold hover:bg-red-50 transition-colors"
            data-testid={`cancel-btn-${booking.booking_id}`}>
            Cancel
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onShare(); }}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 text-[11px] font-semibold hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
          data-testid={`share-btn-${booking.booking_id}`}>
          <ShareNetwork size={12} className="inline mr-1" /> Share
        </button>
        {booking.status === 'completed' && !isReviewed && (
          <button onClick={(e) => { e.stopPropagation(); onReview(); }}
            className="px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold hover:bg-amber-100 transition-colors"
            data-testid={`write-review-${booking.booking_id}`}>
            <Star size={12} weight="fill" className="inline mr-1" /> Review
          </button>
        )}
        {booking.status === 'completed' && isReviewed && (
          <span className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-[11px] font-semibold flex items-center gap-1"
            data-testid={`reviewed-badge-${booking.booking_id}`}>
            <Check size={12} weight="bold" /> Reviewed
          </span>
        )}
        <button onClick={onClick}
          className="ml-auto px-3 py-1.5 rounded-lg bg-blue-500 text-white text-[11px] font-semibold hover:bg-blue-600 transition-colors"
          data-testid={`view-details-${booking.booking_id}`}>
          Details
        </button>
      </div>
    </div>
  );
};

export default MyBookings;
